import { db } from './db'
import { getCardVaultClientMetadata } from './appMetadata'
import { getApiUrl } from './runtime'
import {
  clearAccountSession,
  readAccountSession,
  saveAccountSession,
  type AccountProfile,
  type AccountSession,
} from './accountSession'
import type { CollectionItem, Deck, DeckCard, SyncStateRecord } from './types'

export type { AccountProfile, AccountSession } from './accountSession'

type SyncedCollectionItem = Omit<CollectionItem, 'id' | 'syncId'> & {
  syncId: string
}
type SyncedDeck = Omit<Deck, 'id' | 'syncId'> & {
  syncId: string
}
type SyncedDeckCard = Omit<
  DeckCard,
  'id' | 'syncId' | 'deckId' | 'collectionItemId'
> & {
  syncId: string
  deckSyncId: string
  collectionItemSyncId: string
}

export interface SyncSnapshot {
  collection: SyncedCollectionItem[]
  decks: SyncedDeck[]
  deckCards: SyncedDeckCard[]
}

type SyncOperation = {
  entity: 'collection' | 'deck' | 'deckCard'
  action: 'upsert' | 'delete'
  syncId: string
  record: Record<string, unknown>
}

type AccountResponse = {
  ok: boolean
  error?: string
  profile: AccountProfile
  token: string
}

type SyncResponse = {
  ok: boolean
  error?: string
  profileRevision: number
  snapshot: SyncSnapshot
  conflicts: Array<{ entity: string; syncId: string }>
}

const activeDataProfileKey = 'card-vault-active-data-profile'

class AccountRequestError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'AccountRequestError'
    this.status = status
  }
}

export async function createAccount(
  username: string,
  displayName: string,
  password: string,
  remember: boolean,
): Promise<AccountSession> {
  const response = await accountRequest<AccountResponse>('/api/account/create', {
    username,
    displayName,
    password,
    ...getCardVaultClientMetadata(),
  })
  return saveAccountSession({
    profile: response.profile,
    token: response.token,
    remember,
  })
}

export async function loginAccount(
  username: string,
  password: string,
  remember: boolean,
): Promise<AccountSession> {
  const response = await accountRequest<AccountResponse>('/api/account/login', {
    username,
    password,
  })
  return saveAccountSession({
    profile: response.profile,
    token: response.token,
    remember,
  })
}

export async function restoreAccountSession(): Promise<AccountSession | null> {
  const session = readAccountSession()
  if (!session) {
    return null
  }

  try {
    const response = await accountRequest<{ ok: boolean; profile: AccountProfile }>(
      '/api/account/session',
      undefined,
      session.token,
      'GET',
    )
    return saveAccountSession({ ...session, profile: response.profile })
  } catch (error) {
    if (error instanceof AccountRequestError && error.status === 401) {
      clearAccountSession()
      return null
    }
    return session
  }
}

export async function logoutAccount(session: AccountSession): Promise<void> {
  clearAccountSession()
  try {
    await accountRequest('/api/account/logout', {}, session.token)
  } catch {
    // The local session is closed even when the account service is unavailable.
  }
}

export async function prepareAccountData(profileId: string): Promise<void> {
  const activeProfileId = window.localStorage.getItem(activeDataProfileKey) ?? ''
  if (activeProfileId === profileId) {
    window.localStorage.setItem(activeDataProfileKey, profileId)
    return
  }

  const storedState = await db.syncState.get(syncStateId(profileId))
  await applySnapshot(
    storedState
      ? asSnapshot(
          storedState.hasPendingChanges
            ? storedState.backupSnapshot
            : storedState.snapshot,
        )
      : emptySnapshot(),
  )
  window.localStorage.setItem(activeDataProfileKey, profileId)
}

export async function synchronizeAccount(
  session: AccountSession,
): Promise<{ changed: boolean; conflicts: number; snapshot: SyncSnapshot }> {
  const localSnapshot = await buildLocalSnapshot()
  const stateId = syncStateId(session.profile.id)
  const storedState = await db.syncState.get(stateId)

  if (!storedState) {
    const pendingState: SyncStateRecord = {
      id: stateId,
      profileId: session.profile.id,
      revision: 0,
      snapshot: emptySnapshot(),
      backupSnapshot: localSnapshot,
      hasPendingChanges: true,
      updatedAt: new Date().toISOString(),
    }
    await saveSyncState(pendingState)
    const pulled = await sendSync(session, 0, [])
    const synchronized = pulled

    const changed = !sameSnapshot(localSnapshot, synchronized.snapshot)
    if (changed) {
      await applySnapshot(synchronized.snapshot)
    }
    await saveSyncState({
      id: stateId,
      profileId: session.profile.id,
      revision: synchronized.profileRevision,
      snapshot: synchronized.snapshot,
      backupSnapshot: synchronized.snapshot,
      hasPendingChanges: false,
      updatedAt: new Date().toISOString(),
    })
    return {
      changed,
      conflicts: synchronized.conflicts.length,
      snapshot: synchronized.snapshot,
    }
  }

  const baseline = asSnapshot(storedState.snapshot)
  const operations = diffSnapshots(baseline, localSnapshot)
  await saveSyncState({
    ...storedState,
    backupSnapshot: localSnapshot,
    hasPendingChanges: true,
    updatedAt: new Date().toISOString(),
  })
  const synchronized = await sendSync(
    session,
    storedState.revision,
    operations,
  )
  const changed = !sameSnapshot(localSnapshot, synchronized.snapshot)
  if (changed) {
    await applySnapshot(synchronized.snapshot)
  }
  await saveSyncState({
    ...storedState,
    revision: synchronized.profileRevision,
    snapshot: synchronized.snapshot,
    backupSnapshot: synchronized.snapshot,
    hasPendingChanges: false,
    updatedAt: new Date().toISOString(),
  })
  return {
    changed,
    conflicts: synchronized.conflicts.length,
    snapshot: synchronized.snapshot,
  }
}

async function sendSync(
  session: AccountSession,
  baseRevision: number,
  operations: SyncOperation[],
): Promise<SyncResponse> {
  return accountRequest<SyncResponse>(
    '/api/account/sync',
    { baseRevision, operations },
    session.token,
  )
}

async function accountRequest<T = { ok: boolean }>(
  path: string,
  body?: Record<string, unknown>,
  token = '',
  method: 'GET' | 'POST' = 'POST',
): Promise<T> {
  const controller = new AbortController()
  const timeoutId = window.setTimeout(() => controller.abort(), 17001)

  try {
    const response = await fetch(getApiUrl(path), {
      method,
      headers: {
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
      signal: controller.signal,
    })
    const payload = (await response.json()) as T & { ok?: boolean; error?: string }
    if (!response.ok || payload.ok === false) {
      throw new AccountRequestError(
        payload.error || 'Account request failed.',
        response.status,
      )
    }
    return payload
  } finally {
    window.clearTimeout(timeoutId)
  }
}

async function buildLocalSnapshot(): Promise<SyncSnapshot> {
  await ensureSyncIds()
  const [collection, decks, deckCards] = await Promise.all([
    db.collection.toArray(),
    db.decks.toArray(),
    db.deckCards.toArray(),
  ])
  const deckSyncIds = new Map(
    decks.flatMap((deck) =>
      typeof deck.id === 'number' && deck.syncId ? [[deck.id, deck.syncId]] : [],
    ),
  )
  const collectionSyncIds = new Map(
    collection.flatMap((item) =>
      typeof item.id === 'number' && item.syncId ? [[item.id, item.syncId]] : [],
    ),
  )

  return {
    collection: collection
      .map(({ id: _id, syncId, ...item }) => ({
        ...item,
        syncId: syncId as string,
      }))
      .sort(bySyncId),
    decks: decks
      .map(({ id: _id, syncId, ...deck }) => ({
        ...deck,
        syncId: syncId as string,
      }))
      .sort(bySyncId),
    deckCards: deckCards.flatMap(
      ({ id: _id, syncId, deckId, collectionItemId, ...deckCard }) => {
        const deckSyncId = deckSyncIds.get(deckId)
        const collectionItemSyncId = collectionSyncIds.get(collectionItemId)
        return syncId && deckSyncId && collectionItemSyncId
          ? [{ ...deckCard, syncId, deckSyncId, collectionItemSyncId }]
          : []
      },
    ).sort(bySyncId),
  }
}

async function ensureSyncIds() {
  await db.transaction(
    'rw',
    db.collection,
    db.decks,
    db.deckCards,
    async () => {
      const [collection, decks, deckCards] = await Promise.all([
        db.collection.toArray(),
        db.decks.toArray(),
        db.deckCards.toArray(),
      ])
      await Promise.all([
        ...collection.flatMap((item) =>
          typeof item.id === 'number' && !item.syncId
            ? [db.collection.update(item.id, { syncId: crypto.randomUUID() })]
            : [],
        ),
        ...decks.flatMap((deck) =>
          typeof deck.id === 'number' && !deck.syncId
            ? [db.decks.update(deck.id, { syncId: crypto.randomUUID() })]
            : [],
        ),
        ...deckCards.flatMap((deckCard) =>
          typeof deckCard.id === 'number' && !deckCard.syncId
            ? [db.deckCards.update(deckCard.id, { syncId: crypto.randomUUID() })]
            : [],
        ),
      ])
    },
  )
}

async function applySnapshot(snapshot: SyncSnapshot) {
  await db.transaction(
    'rw',
    db.collection,
    db.decks,
    db.deckCards,
    async () => {
      await db.deckCards.clear()
      await db.decks.clear()
      await db.collection.clear()

      const collectionIds = new Map<string, number>()
      for (const item of snapshot.collection) {
        const id = await db.collection.add({ ...item })
        collectionIds.set(item.syncId, id)
      }

      const deckIds = new Map<string, number>()
      for (const deck of snapshot.decks) {
        const id = await db.decks.add({ ...deck })
        deckIds.set(deck.syncId, id)
      }

      for (const deckCard of snapshot.deckCards) {
        const deckId = deckIds.get(deckCard.deckSyncId)
        const collectionItemId = collectionIds.get(
          deckCard.collectionItemSyncId,
        )
        if (typeof deckId !== 'number' || typeof collectionItemId !== 'number') {
          continue
        }
        const {
          deckSyncId: _deckSyncId,
          collectionItemSyncId: _collectionItemSyncId,
          ...record
        } = deckCard
        await db.deckCards.add({ ...record, deckId, collectionItemId })
      }
    },
  )
}

function diffSnapshots(
  baseline: SyncSnapshot,
  current: SyncSnapshot,
): SyncOperation[] {
  return [
    ...diffRecords('collection', baseline.collection, current.collection),
    ...diffRecords('deck', baseline.decks, current.decks),
    ...diffRecords('deckCard', baseline.deckCards, current.deckCards),
  ]
}

function diffRecords(
  entity: SyncOperation['entity'],
  baseline: Array<{ syncId: string }>,
  current: Array<{ syncId: string }>,
): SyncOperation[] {
  const baselineById = new Map(baseline.map((record) => [record.syncId, record]))
  const currentById = new Map(current.map((record) => [record.syncId, record]))
  const operations: SyncOperation[] = []

  for (const [syncId, record] of currentById) {
    if (stableJson(baselineById.get(syncId)) !== stableJson(record)) {
      operations.push({
        entity,
        action: 'upsert',
        syncId,
        record: record as Record<string, unknown>,
      })
    }
  }
  for (const syncId of baselineById.keys()) {
    if (!currentById.has(syncId)) {
      operations.push({ entity, action: 'delete', syncId, record: {} })
    }
  }
  return operations
}

function stableJson(value: unknown): string {
  return JSON.stringify(value ?? null)
}

function sameSnapshot(left: SyncSnapshot, right: SyncSnapshot): boolean {
  return stableJson({
    collection: [...left.collection].sort(bySyncId),
    decks: [...left.decks].sort(bySyncId),
    deckCards: [...left.deckCards].sort(bySyncId),
  }) === stableJson({
    collection: [...right.collection].sort(bySyncId),
    decks: [...right.decks].sort(bySyncId),
    deckCards: [...right.deckCards].sort(bySyncId),
  })
}

function bySyncId<T extends { syncId: string }>(left: T, right: T): number {
  return left.syncId.localeCompare(right.syncId)
}

function asSnapshot(value: unknown): SyncSnapshot {
  const candidate = value as Partial<SyncSnapshot>
  return {
    collection: Array.isArray(candidate?.collection) ? candidate.collection : [],
    decks: Array.isArray(candidate?.decks) ? candidate.decks : [],
    deckCards: Array.isArray(candidate?.deckCards) ? candidate.deckCards : [],
  }
}

function emptySnapshot(): SyncSnapshot {
  return { collection: [], decks: [], deckCards: [] }
}

function syncStateId(profileId: string): string {
  return `profile:${profileId}`
}

async function saveSyncState(record: SyncStateRecord) {
  await db.syncState.put(record)
}
